/**
 * AdminKnowledgeHub — Admin Hub content management for Knowledge Hub
 *
 * Features:
 *  - List all documents (published + unpublished)
 *  - Upload new PDF or video (pre-signed S3 PUT URL flow)
 *  - Edit metadata (title, category, published toggle)
 *  - Delete documents
 */

import { useState, useEffect, useRef } from 'react';
import { getAPIBase, getAuthHeaders } from '../lib/api-fetch';
import { buildPath } from '../lib/api-endpoints';
import type { KnowledgeHubDocument } from '../lib/api-endpoints';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, FileText, Video, RotateCcw, Upload, Eye, EyeOff,
} from 'lucide-react';

const CATEGORIES = [
  'Standard Operating Procedures',
  'Training Materials',
  'Application Forms',
  'Company Policies & Manuals',
  'Marketing & Templates',
  'Legal & Compliance',
  'Video Training',
] as const;

type Category = typeof CATEGORIES[number];

interface UploadForm {
  title: string;
  category: Category;
  subcategory: string;
  description: string;
  fileType: 'pdf' | 'video';
  published: boolean;
  sortOrder: number;
}

const DEFAULT_FORM: UploadForm = {
  title: '',
  category: 'Standard Operating Procedures',
  subcategory: '',
  description: '',
  fileType: 'pdf',
  published: false,
  sortOrder: 0,
};

export function AdminKnowledgeHub() {
  const [documents, setDocuments] = useState<KnowledgeHubDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState<UploadForm>(DEFAULT_FORM);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editingDoc, setEditingDoc] = useState<KnowledgeHubDocument | null>(null);
  const [editForm, setEditForm] = useState<Partial<UploadForm>>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const qs = categoryFilter ? `?category=${encodeURIComponent(categoryFilter)}` : '';
      const res = await fetch(`${getAPIBase()}/admin/knowledge-hub${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setDocuments(json.data.documents || []);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadDocuments(); }, [categoryFilter]);

  const handleUploadSubmit = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    if (!uploadForm.title) { toast.error('Title is required'); return; }

    setIsUploading(true);
    try {
      // Step 1: Create record and get pre-signed URL
      const payload = {
        title: uploadForm.title,
        category: uploadForm.category,
        subcategory: uploadForm.subcategory || undefined,
        description: uploadForm.description || undefined,
        fileType: uploadForm.fileType,
        fileName: selectedFile.name,
        contentType: selectedFile.type,
        fileSizeMb: parseFloat((selectedFile.size / (1024 * 1024)).toFixed(2)),
        published: uploadForm.published,
        sortOrder: uploadForm.sortOrder,
      };

      const createRes = await fetch(`${getAPIBase()}/admin/knowledge-hub`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const createJson = await createRes.json();
      if (!createJson.success) throw new Error(createJson.error?.message || 'Create failed');

      const { uploadUrl } = createJson.data;

      // Step 2: Upload file directly to S3
      const s3Res = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: { 'Content-Type': selectedFile.type },
      });
      if (!s3Res.ok) throw new Error('S3 upload failed');

      toast.success(`"${uploadForm.title}" uploaded successfully`);
      setIsUploadOpen(false);
      setUploadForm(DEFAULT_FORM);
      setSelectedFile(null);
      await loadDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingDoc) return;
    setIsSavingEdit(true);
    try {
      const path = buildPath('/admin/knowledge-hub/:id', { id: editingDoc.documentId });
      const res = await fetch(`${getAPIBase()}${path}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Update failed');
      toast.success('Document updated');
      setEditingDoc(null);
      await loadDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Update failed');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async (doc: KnowledgeHubDocument) => {
    if (!confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    setDeletingId(doc.documentId);
    try {
      const path = buildPath('/admin/knowledge-hub/:id', { id: doc.documentId });
      const res = await fetch(`${getAPIBase()}${path}`, { method: 'DELETE', headers: getAuthHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Delete failed');
      toast.success('Document deleted');
      await loadDocuments();
    } catch (err: any) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = documents.filter(d => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (d.title || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Knowledge Hub Management</h2>
          <p className="text-sm text-gray-500 mt-1">Upload and manage training materials, policies, and resources.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadDocuments} disabled={isLoading} data-testid="admin-kh-refresh">
            <RotateCcw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setIsUploadOpen(true)} data-testid="admin-kh-upload-trigger">
            <Plus className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="admin-kh-search"
          />
        </div>
        <Select value={categoryFilter || 'all'} onValueChange={v => setCategoryFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-56" data-testid="admin-kh-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Document list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No documents found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(doc => (
            <div key={doc.documentId} data-testid={`admin-kh-doc-${doc.documentId}`}
              className="flex items-center justify-between p-4 rounded-lg border bg-white">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`flex-shrink-0 w-9 h-9 rounded flex items-center justify-center ${doc.fileType === 'video' ? 'bg-indigo-100' : 'bg-blue-100'}`}>
                  {doc.fileType === 'video'
                    ? <Video className="w-4 h-4 text-indigo-600" />
                    : <FileText className="w-4 h-4 text-blue-600" />
                  }
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{doc.title}</p>
                    {doc.published
                      ? <Badge className="bg-green-100 text-green-700 text-xs">Published</Badge>
                      : <Badge variant="secondary" className="text-xs">Draft</Badge>
                    }
                  </div>
                  <p className="text-xs text-gray-500">{doc.category}{doc.subcategory ? ` › ${doc.subcategory}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <Button variant="ghost" size="icon"
                  onClick={() => { setEditingDoc(doc); setEditForm({ title: doc.title, category: doc.category as Category, subcategory: doc.subcategory || '', description: doc.description || '', published: doc.published, sortOrder: doc.sortOrder }); }}
                  data-testid={`admin-kh-edit-${doc.documentId}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700"
                  onClick={() => handleDelete(doc)}
                  disabled={deletingId === doc.documentId}
                  data-testid={`admin-kh-delete-${doc.documentId}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={open => { setIsUploadOpen(open); if (!open) { setUploadForm(DEFAULT_FORM); setSelectedFile(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input value={uploadForm.title} onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))} placeholder="Document title" data-testid="admin-kh-upload-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Category *</Label>
                <Select value={uploadForm.category} onValueChange={v => setUploadForm(f => ({ ...f, category: v as Category }))}>
                  <SelectTrigger data-testid="admin-kh-upload-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>File Type *</Label>
                <Select value={uploadForm.fileType} onValueChange={v => setUploadForm(f => ({ ...f, fileType: v as 'pdf' | 'video' }))}>
                  <SelectTrigger data-testid="admin-kh-upload-filetype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Subcategory (optional)</Label>
              <Input value={uploadForm.subcategory} onChange={e => setUploadForm(f => ({ ...f, subcategory: e.target.value }))} placeholder="e.g., Onboarding" />
            </div>
            <div className="space-y-1">
              <Label>Description (optional)</Label>
              <Textarea rows={2} value={uploadForm.description} onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" />
            </div>
            <div className="space-y-1">
              <Label>File *</Label>
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file"
                  accept={uploadForm.fileType === 'pdf' ? '.pdf,application/pdf' : '.mp4,.mov,video/mp4,video/quicktime'}
                  className="hidden"
                  onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                  data-testid="admin-kh-file-input"
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} type="button">
                  <Upload className="w-4 h-4 mr-2" />
                  {selectedFile ? selectedFile.name : 'Choose file'}
                </Button>
                {selectedFile && <span className="text-sm text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={uploadForm.published} onCheckedChange={v => setUploadForm(f => ({ ...f, published: v }))} id="upload-published" data-testid="admin-kh-upload-published" />
              <Label htmlFor="upload-published">Publish immediately</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
              <Button onClick={handleUploadSubmit} disabled={isUploading} data-testid="admin-kh-upload-submit">
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={open => { if (!open) setEditingDoc(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Title</Label>
              <Input value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} data-testid="admin-kh-edit-title" />
            </div>
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={editForm.category || ''} onValueChange={v => setEditForm(f => ({ ...f, category: v as Category }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea rows={2} value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editForm.published ?? false} onCheckedChange={v => setEditForm(f => ({ ...f, published: v }))} id="edit-published" data-testid="admin-kh-edit-published" />
              <Label htmlFor="edit-published">Published</Label>
              {editForm.published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={isSavingEdit} data-testid="admin-kh-edit-save">
                {isSavingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
